import {
    Card, CardContent, CardHeader, Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import React from "react";
import {formatRFC7231, SmartLink} from "../abelcommon";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {InputItem, OutputsItem} from './TransactionItem'
import {useTx} from "../queries";
import QueryProcessor from "./QueryProcessor";
import useSummary from "../queries/useSummary";


interface TxItemProps {
    txid: string;
    inputs: any;
    outputs: any;
}

function TxItem({txid, inputs, outputs}: TxItemProps) {
    return (
        <Card sx={{
            margin: 1,
            padding: 1,
            display: 'flex',
            boxShadow: 3,
            borderRadius: 2,
            width: 1,
            overflow: 'auto',
        }}>
            <Grid container  direction="row" spacing={1}>
                <Grid item xs={12} md={7}>
                    <Card>
                        <CardHeader
                            title={<Typography variant={'h5'} textAlign={'center'}>{`Inputs`}</Typography>}
                            sx={{p: 0}}
                        />
                        <CardContent sx={{px:0,maxHeight: 250, overflow: 'auto'}}>
                            {inputs.map((input: any, i: any) => (
                                <InputItem key={`${txid}_input_${i}`} ringid={input.ringid}
                                           outpoints={input.outpoints}
                                           serialnumber={input.serialnumber}
                                />
                            ))}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={1}>
                    <Stack sx={{height: '100%', alignItems: 'center', justifyContent: 'center'}}>
                        <KeyboardArrowRightIcon fontSize={"large"} sx={{
                            display: {
                                xs: 'none',
                                md: 'block'
                            }
                        }}/>
                        <KeyboardArrowDownIcon fontSize={"large"} sx={{
                            display: {
                                xs: 'block',
                                md: 'none'
                            }
                        }}/>
                    </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardHeader
                            title={<Typography variant={'h5'} textAlign={'center'}>{`Outputs`}</Typography>}
                            sx={{p: 0}}
                        />
                        <CardContent sx={{maxHeight: 220, overflow: 'auto'}}>
                            <OutputsItem
                                key={`${txid}_outpoints`}
                                outputs={outputs}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Card>
    );
}


function TxRow({address, data, latestHeight, i, allowExpand}: {
    address?: string
    data: any,
    latestHeight: number,
    i: number,
    allowExpand: boolean
}) {
    const dts = data.time * 1000;

    const [open, setOpen] = React.useState(false);

    if (address && data.txtype === 'transfer') {
        scanVin:
            for (const vin of data.vin) {
                for (const outpoint of vin.outpoints) {
                    if (outpoint.address === address.substring(2)) {
                        data.txtype = "transfer out";
                        break scanVin;
                    }
                }
            }
        for (const vout of data.vout) {
            if (vout.address === address.substring(2)) {
                if (data.txtype === "transfer out") {
                    data.txtype = "transfer";
                } else {
                    data.txtype = "transfer in";
                }
                break;
            }
        }
    }

    return (
        <>
            <TableRow key={data.txid} onClick={() => setOpen(!open)} sx={{height: 60}}>
                <TableCell>
                    <SmartLink to={`/tx/0x${data.txid}`}>
                        0x{data.txid.slice(0, 8) + '..' + data.txid.slice(-8)}
                    </SmartLink>
                </TableCell>
                <TableCell>
                    <SmartLink to={`/block/${data.blockheight}`}>{data.blockheight}</SmartLink>
                </TableCell>
                <TableCell align={'center'}>{formatRFC7231(dts).substring(5)}</TableCell>
                <TableCell align={'right'} sx={{
                    display: {
                        xs: 'none', // theme.breakpoints.up('sm')
                        sm: 'none', // theme.breakpoints.up('sm')
                        md: 'table-cell', // theme.breakpoints.up('md')
                        // lg: 'table-cell', // theme.breakpoints.up('lg')
                        // xl: 'table-cell', // theme.breakpoints.up('xl')
                    }
                }}>
                    {data.txtype}
                </TableCell>
                <TableCell align={'right'} sx={{
                    display: {
                        xs: 'none', // theme.breakpoints.up('sm')
                        sm: 'none', // theme.breakpoints.up('sm')
                        md: 'table-cell', // theme.breakpoints.up('md')
                        // lg: 'table-cell', // theme.breakpoints.up('lg')
                        // xl: 'table-cell', // theme.breakpoints.up('xl')
                    }
                }}>
                    {data.vin.length}
                </TableCell>
                <TableCell align={'right'} sx={{
                    display: {
                        xs: 'none', // theme.breakpoints.up('sm')
                        sm: 'none', // theme.breakpoints.up('sm')
                        md: 'table-cell', // theme.breakpoints.up('md')
                        // lg: 'table-cell', // theme.breakpoints.up('lg')
                        // xl: 'table-cell', // theme.breakpoints.up('xl')
                    }
                }}>
                    {data.vout.length}
                </TableCell>
                <TableCell align={'right'} sx={{
                    display: {
                        xs: 'none', // theme.breakpoints.up('sm')
                        sm: 'none', // theme.breakpoints.up('sm')
                        md: 'table-cell', // theme.breakpoints.up('md')
                        // lg: 'table-cell', // theme.breakpoints.up('lg')
                        // xl: 'table-cell', // theme.breakpoints.up('xl')
                    }
                }}>
                    {data.fee} ABEL
                </TableCell>
                <TableCell align={'right'} sx={{
                    display: {
                        xs: 'none', // theme.breakpoints.up('sm')
                        sm: 'none', // theme.breakpoints.up('sm')
                        md: 'table-cell', // theme.breakpoints.up('md')
                        // lg: 'table-cell', // theme.breakpoints.up('lg')
                        // xl: 'table-cell', // theme.breakpoints.up('xl')
                    }
                }}>
                    {latestHeight - data.blockheight}
                </TableCell>
                {allowExpand && <TableCell sx={{
                    display: {
                        xs: 'none', // theme.breakpoints.up('sm')
                        sm: 'none', // theme.breakpoints.up('sm')
                        md: 'table-cell', // theme.breakpoints.up('md')
                        // lg: 'table-cell', // theme.breakpoints.up('lg')
                        // xl: 'table-cell', // theme.breakpoints.up('xl')
                    }
                }}>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        sx={{bgcolor: 'transparent'}}
                        // onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
                    </IconButton>
                </TableCell>}
            </TableRow>
            {allowExpand && open && <ExpandedTxRow txid={data.txid}/>}
        </>
    );
}

function ExpandedTxRow({txid}: { txid: string }) {
    const txDetailQuery = useTx(txid);
    return <QueryProcessor
        query={txDetailQuery}
        dataValidator={(data: any) => data.type === 'txdetail'}
        renderer={(data: any) => (
            <TableRow key={`expand_tx_${data.txid}`}>
                <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={9}>
                    <Stack direction='column' spacing={1}>
                        <TxItem key={`block_${data}_tx_${data.txid}`}
                                txid={data.payload.txid} inputs={data.payload.vin}
                                outputs={data.payload.vout}
                        />
                    </Stack>
                </TableCell>
            </TableRow>)}
    />
}

interface TxListProps {
    address?: string,
    data: any[];
    allowExpand?: boolean;
}

function TxList({address, data, allowExpand = true}: TxListProps) {
    const summary = useSummary();
    return (
        <QueryProcessor
            query={summary}
            dataValidator={(data: any) => data.type === 'summary'}
            renderer={(summaryData: any) => {
                return (
                    <TableContainer>
                        <Table
                            sx={{
                                width: '100%',
                                tableLayout: 'auto',
                                '& .MuiTableCell-root': {px: 1, py: 1},
                            }}
                        >
                            <TableHead sx={{'& .MuiTableCell-root': {fontWeight: 'bold'}}}>
                                <TableRow>
                                    <TableCell>
                                        Hash (txid)
                                    </TableCell>
                                    <TableCell>
                                        Block
                                    </TableCell>
                                    <TableCell align={'center'}>
                                        Timestamp
                                    </TableCell>
                                    <TableCell align={'right'} sx={{
                                        display: {
                                            xs: 'none', // theme.breakpoints.up('sm')
                                            sm: 'none', // theme.breakpoints.up('sm')
                                            md: 'table-cell', // theme.breakpoints.up('md')
                                            // lg: 'table-cell', // theme.breakpoints.up('lg')
                                            // xl: 'table-cell', // theme.breakpoints.up('xl')
                                        }
                                    }}>
                                        Type
                                    </TableCell>
                                    <TableCell align={'right'} sx={{
                                        display: {
                                            xs: 'none', // theme.breakpoints.up('sm')
                                            sm: 'none', // theme.breakpoints.up('sm')
                                            md: 'table-cell', // theme.breakpoints.up('md')
                                            // lg: 'table-cell', // theme.breakpoints.up('lg')
                                            // xl: 'table-cell', // theme.breakpoints.up('xl')
                                        }
                                    }}>
                                        Inputs
                                    </TableCell>
                                    <TableCell align={'right'} sx={{
                                        display: {
                                            xs: 'none', // theme.breakpoints.up('sm')
                                            sm: 'none', // theme.breakpoints.up('sm')
                                            md: 'table-cell', // theme.breakpoints.up('md')
                                            // lg: 'table-cell', // theme.breakpoints.up('lg')
                                            // xl: 'table-cell', // theme.breakpoints.up('xl')
                                        }
                                    }}>
                                        Outputs
                                    </TableCell>
                                    <TableCell align={'right'} sx={{
                                        display: {
                                            xs: 'none', // theme.breakpoints.up('sm')
                                            sm: 'none', // theme.breakpoints.up('sm')
                                            md: 'table-cell', // theme.breakpoints.up('md')
                                            // lg: 'table-cell', // theme.breakpoints.up('lg')
                                            // xl: 'table-cell', // theme.breakpoints.up('xl')
                                        }
                                    }}>
                                        Value
                                    </TableCell>
                                    <TableCell align={'right'} sx={{
                                        display: {
                                            xs: 'none', // theme.breakpoints.up('sm')
                                            sm: 'none', // theme.breakpoints.up('sm')
                                            md: 'table-cell', // theme.breakpoints.up('md')
                                            // lg: 'table-cell', // theme.breakpoints.up('lg')
                                            // xl: 'table-cell', // theme.breakpoints.up('xl')
                                        }
                                    }}>
                                        Confirmations
                                    </TableCell>
                                    {allowExpand && <TableCell sx={{
                                        display: {
                                            xs: 'none', // theme.breakpoints.up('sm')
                                            sm: 'none', // theme.breakpoints.up('sm')
                                            md: 'table-cell', // theme.breakpoints.up('md')
                                            // lg: 'table-cell', // theme.breakpoints.up('lg')
                                            // xl: 'table-cell', // theme.breakpoints.up('xl')
                                        }
                                    }}></TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((d: any, i: number) => (
                                    <TxRow key={d.txid}
                                           latestHeight={summaryData.payload.latestBlockHeight}
                                           data={d}
                                           i={i}
                                           allowExpand={allowExpand}
                                           address={address}
                                    />)
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )
            }}
        />
    );
}

export default TxList;